import { Dtos, type Rate, SuretyQuoteStatus } from "@inrev/common";
import { useAdminGetCarrierPreviews } from "@portal/api/account/admin";
import {
	useAdminFetchCarrierAgencyRelationships,
	useAdminUpdateBondRequestStatus,
} from "@portal/api/bond-request/admin";
import {
	AdminBondRequest,
	AdminUpdateBondRequestFormData,
} from "@portal/api/bond-request/admin/types";
import { FormField, FormFieldGroup, FormItemLabel, FormRow } from "@portal/components/form";
import { FormDropdown, FormPercent } from "@portal/components/form/fields";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { cn } from "@portal/components/lib/utils";
import { Button } from "@portal/components/ui/Button";
import { useEffect } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { stripEmptyResolver, typedZodResolver } from "../../../utils/form";
import {
	AdminApproveBondFormFormFields,
	getDefaultFormData as getApproveBondFormDefaultFormData,
} from "./AdminApproveBondFormForAgentIssueForm";

type AdminUpdateBondRequestFormProps = {
	request: AdminBondRequest & {
		status: Extract<SuretyQuoteStatus, "approved" | "accepted">;
	};
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response;
	accountRate: Rate | undefined;
	onSubmit?: () => void;
	className?: string;
};

const getDefaultFormData = (
	request: AdminBondRequest,
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response,
	status: Extract<SuretyQuoteStatus, "approved">,
	shouldApproveBondFormForAgentIssue: boolean,
	accountRate: Rate | undefined,
): AdminUpdateBondRequestFormData => ({
	suretyType: request.suretyType,
	contractSuretyType: request.contractSuretyType,
	status,
	carrierName: request.carrier?.name ?? "",
	rate: request.rate ?? accountRate ?? undefined,
	finalBondRate:
		request.contractSuretyType === "bid"
			? (request.finalBondContingentRate ?? accountRate ?? undefined)
			: (accountRate ?? undefined),
	agencyCommissionPercentage: request.agencyCommissionPercentage ?? undefined,
	reviewNote: request.reviewNote ?? "",
	...(() => {
		if (shouldApproveBondFormForAgentIssue) {
			return {
				bondForm: getApproveBondFormDefaultFormData(request, bondForm),
			};
		}
		return {};
	})(),
});

export const AdminUpdateBondRequestTermsForm = ({
	request,
	bondForm,
	accountRate,
	onSubmit,
	className,
}: AdminUpdateBondRequestFormProps) => {
	const { updateBondRequestStatus, updateBondRequestStatusLoading } =
		useAdminUpdateBondRequestStatus(request.id, request.suretyAccountId);
	const { allCarriers } = useAdminGetCarrierPreviews();
	const { carrierAgencyRelationships } = useAdminFetchCarrierAgencyRelationships(request.agencyId);

	const shouldApproveBondFormForAgentIssue =
		bondForm.source === "upload" && !bondForm.approvedBeforeIssuance;

	const formMethods = useForm<
		AdminUpdateBondRequestFormData,
		// biome-ignore lint/suspicious/noExplicitAny: legacy any
		any,
		Dtos.Admin.SuretyQuote.UpdateStatus.Request
	>({
		defaultValues: getDefaultFormData(
			request,
			bondForm,
			SuretyQuoteStatus.approved,
			shouldApproveBondFormForAgentIssue,
			accountRate,
		),
		reValidateMode: "onBlur",
		resolver: stripEmptyResolver(
			typedZodResolver(Dtos.Admin.SuretyQuote.UpdateStatus.Request.schema),
		),
	});
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	const widenedFormMethods: UseFormReturn<AdminUpdateBondRequestFormData, any, any> = formMethods;

	const handleSubmit = (data: Dtos.Admin.SuretyQuote.UpdateStatus.Request) => {
		updateBondRequestStatus({ data });
	};

	useEffect(() => {
		if (onSubmit && !updateBondRequestStatusLoading && formMethods.formState.submitCount > 0) {
			onSubmit();
		}
	}, [updateBondRequestStatusLoading]);

	const carrier = formMethods.watch("carrierName");
	useEffect(() => {
		if (carrier && !request.agencyCommissionPercentage) {
			const carrierRelationship = carrierAgencyRelationships?.find(
				(cr) => cr.carrierName === carrier,
			);
			formMethods.setValue(
				"agencyCommissionPercentage",
				carrierRelationship?.agencyCommissionPercentage ?? undefined,
			);
		}
	}, [carrier, formMethods.setValue, carrierAgencyRelationships]);

	return (
		<div className={cn("px-[40px] pb-[30px]", className)}>
			<form>
				<FormFieldGroup>
					{allCarriers && (
						<>
							<FormField>
								<FormItemLabel required>Carrier</FormItemLabel>
								<FormDropdown
									name="carrierName"
									control={widenedFormMethods.control}
									options={allCarriers.map((carrier) => ({
										value: carrier.name,
										label: carrier.legalName,
									}))}
									placeholder="Select one"
									compact
								/>
							</FormField>
							<FormRow>
								<FormField>
									<FormItemLabel required>
										{request.contractSuretyType === "bid" ? "Final Bond Rate" : "Rate"}
									</FormItemLabel>
									<FormPercent
										name={request.contractSuretyType === "bid" ? "finalBondRate" : "rate"}
										control={widenedFormMethods.control}
										compact
										decimalPlaces={5}
									/>
								</FormField>
								<FormField>
									<FormItemLabel required>Agency Commission</FormItemLabel>
									<FormPercent
										name="agencyCommissionPercentage"
										control={widenedFormMethods.control}
										compact
									/>
								</FormField>
							</FormRow>
							{shouldApproveBondFormForAgentIssue && (
								<AdminApproveBondFormFormFields
									formMethods={formMethods}
									fieldPaths={{
										bondFormType: "bondForm.bondFormType",
										cancellable: "bondForm.cancellable",
										continuous: "bondForm.continuous",
									}}
									request={request}
								/>
							)}
						</>
					)}
					<div className="flex w-full justify-end pt-[10px]">
						<ConfirmationModalClickWrapper
							message="Are you sure?"
							onConfirm={formMethods.handleSubmit(handleSubmit)}
						>
							<Button color="confirm" className="px-[25px] py-[12px]" filled>
								Save Terms
							</Button>
						</ConfirmationModalClickWrapper>
					</div>
				</FormFieldGroup>
			</form>
			{updateBondRequestStatusLoading && <LoadingModal />}
		</div>
	);
};
